import {getTestProps} from '@benchling/js-test-utils';
import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import StepConfigEditorRouter from 'pipelines/generalized-step-config-editor/editors/StepConfigEditorRouter';
import {StepConfigEditorContext} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {
  isShapeUISchema,
  StepConfigUISchema,
  FIELD_ORDER_UI_SCHEMA_KEY,
  isFieldUISchemaActionButton,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {isValueValid, PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {JSONObject, JSONValue} from 'util/Types';

import {useDataLayoutContext} from './DataLayoutContext';
import {
  ConfigFormAction,
  ConfigFormBlock as ConfigFormBlockType,
  ConfigFormFieldEvent,
  LayoutPath,
} from './DataLayoutTypes';

const draftConfigFormGlobalState = makeUseGlobalClientState<JSONObject>({});

/** @package */
export interface ConfigFormBlockProps {
  block: ConfigFormBlockType;
  layoutPath: LayoutPath;
}

export default function ConfigFormBlock({
  block: {configFields, uiSchema},
  layoutPath,
}: ConfigFormBlockProps): JSX.Element {
  const {clientId, onEvents} = useDataLayoutContext();

  assert(configFields.length > 0, 'Expected at least one config field');

  const blockClientStateId = useMemo(() => `${clientId}-${layoutPath.join('-')}`, [clientId, layoutPath]);

  const [draftConfigFormState, setDraftConfigFormState] =
    draftConfigFormGlobalState.useGlobalClientState(blockClientStateId);

  const config = Object.fromEntries(
    configFields.map(({config, key}) => [
      key,
      draftConfigFormState.hasOwnProperty(key) ? (draftConfigFormState[key] ?? null) : config,
    ])
  );
  const configSpec: PipelineConfigJsonSpec = useMemo(
    () => ({
      kind: 'shape',
      fields: Object.fromEntries(configFields.map(({configShape, key}) => [key, configShape])) as Record<
        string,
        PipelineConfigJsonSpec
      >,
      required: true,
    }),
    [configFields]
  );

  const {submit, reset} = useMemo(
    () => (onEvents != null ? hasSubmitOrReset(uiSchema) : {submit: false, reset: false}),
    [uiSchema, onEvents]
  );
  assert(!reset || submit, 'If reset options are specified, submit options must also be specified');

  const dataFramesInfos = useMemo(
    () =>
      Object.fromEntries(
        configFields.map(({configDataFrameInfo, key}) => [
          key,
          configDataFrameInfo != null ? [configDataFrameInfo] : [],
        ])
      ),
    [configFields]
  );

  const inputDataFrames = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(dataFramesInfos).map(([key, configDataFrameInfo]) => [
          key,
          configDataFrameInfo.map(({dataFrameSpec}) => dataFrameSpec),
        ])
      ),
    [dataFramesInfos]
  );

  const isConfigFormStateValid = useMemo(
    () => isValueValid(config, configSpec, inputDataFrames),
    [config, configSpec, inputDataFrames]
  );

  const contextValue = useMemo(() => {
    const actions: Record<string, () => void> = {};
    if (submit && isConfigFormStateValid) {
      actions[ConfigFormAction.SUBMIT] = () => {
        if (Object.keys(draftConfigFormState).length === 0) {
          return;
        }
        assert(onEvents != null, 'Expected onEvents to be defined when calling handleOnSubmit');

        onEvents(
          configFields
            .map(({key}, index) =>
              draftConfigFormState.hasOwnProperty(key)
                ? {
                    type: 'CONFIG_FORM_FIELD',
                    layoutPath: [...layoutPath, 'configFields', index],
                    newValue: draftConfigFormState[key],
                  }
                : null
            )
            .filter((maybeEvent): maybeEvent is ConfigFormFieldEvent => maybeEvent != null)
        );
        setDraftConfigFormState({});
      };
    }
    if (reset) {
      actions[ConfigFormAction.RESET] = () => {
        // Resetting isn't just clearing out the draft state, which would reset any user input in the
        // current session before submitting, but explicitly setting values to null, which resets the configuration back
        // to before any user input at all
        const resetConfigFormState = Object.fromEntries(
          Object.entries(draftConfigFormState).map(([configKey, previousValue]) => {
            const configField = configFields.find((field) => field.key === configKey)!;
            if (configField.configShape.required) {
              return [configKey, previousValue];
            } else {
              return [configKey, null];
            }
          })
        );
        setDraftConfigFormState(resetConfigFormState);
      };
    }
    return {
      dataFramesInfos,
      clientStateId: clientId,
      actions,
    };
  }, [
    configFields,
    clientId,
    dataFramesInfos,
    draftConfigFormState,
    isConfigFormStateValid,
    layoutPath,
    onEvents,
    reset,
    setDraftConfigFormState,
    submit,
  ]);

  return (
    <div
      className={css(styles.configFormBlockContainer)}
      {...getTestProps({component: 'ConfigFormBlock', element: 'root'})}
    >
      <StepConfigEditorContext.Provider value={contextValue}>
        <StepConfigEditorRouter
          config={config}
          configShape={configSpec}
          uiSchema={uiSchema}
          disabled={onEvents == null}
          onChangeConfig={
            onEvents == null
              ? noop
              : (_: JSONValue, path: Array<string>, newValue: JSONValue) => {
                  const configFormField = configFields.find(({key}) => key === path[0]);
                  const configFormFieldIndex = configFields.findIndex(({key}) => key === path[0]);
                  assert(configFormFieldIndex > -1, 'Expected configFormFieldIndex to be defined');
                  assert(configFormField != null, 'Expected configFormField to be defined');

                  if (submit) {
                    setDraftConfigFormState({
                      ...draftConfigFormState,
                      [configFormField.key]: newValue,
                    });
                  } else {
                    onEvents([
                      {
                        type: 'CONFIG_FORM_FIELD',
                        layoutPath: [...layoutPath, 'configFields', configFormFieldIndex],
                        newValue,
                      },
                    ]);
                  }
                }
          }
        />
      </StepConfigEditorContext.Provider>
    </div>
  );
}

const styles = StyleSheet.create({
  configFormBlockContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
});

function hasSubmitOrReset(uiSchema: StepConfigUISchema): {submit: boolean; reset: boolean} {
  if (isFieldUISchemaActionButton(uiSchema)) {
    return {submit: uiSchema.key === ConfigFormAction.SUBMIT, reset: uiSchema.key === ConfigFormAction.RESET};
  }

  const baseSubmitOrReset = {submit: false, reset: false};
  if (!isShapeUISchema(uiSchema)) {
    return baseSubmitOrReset;
  }

  return uiSchema[FIELD_ORDER_UI_SCHEMA_KEY].reduce((submitAndReset, fieldName) => {
    const fieldHasSubmitOrReset = hasSubmitOrReset(uiSchema[fieldName]);
    return {
      submit: submitAndReset.submit || fieldHasSubmitOrReset.submit,
      reset: submitAndReset.reset || fieldHasSubmitOrReset.reset,
    };
  }, baseSubmitOrReset);
}
